import { io } from 'socket.io-client';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Create Socket.IO client instance
// Session-48i (Bug B3): tightened reconnection + heartbeat so the mobile
// handler session doesn't go "brown" on cellular network hops. Key changes:
//   * reconnectionDelayMax caps the exponential back-off so idle sockets
//     wake up in ≤ 5s instead of 30s+ later
//   * timeout raised to 20s so slow-3G handshakes don't get killed
//
// June-2026 (multi-pod transport fix): production runs 2 backend pods behind
// an ingress WITHOUT Socket.IO session affinity. HTTP long-polling keeps a
// per-pod Engine.IO session in memory that Redis does NOT share, so polling
// handshakes that bounce between pods produce "unknown session id" churn →
// the "message only shows after I reopen the chat" + laggy double-tick bug.
// Forcing a single persistent WebSocket (no polling) pins each client to one
// pod for the life of the connection; Redis still fans out across pods. This
// is the standard multi-pod-without-sticky-sessions fix.
const socket = io(API, {
  autoConnect: false,
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  reconnectionAttempts: Infinity,
  timeout: 20000,
  transports: ['websocket'],
  upgrade: false,
  rememberUpgrade: true,
  path: '/api/socket.io/'  // Route through /api/* for Kubernetes ingress
});

// Session-48i (Bug B3): app-level heartbeat. Even when the browser tab is
// backgrounded on mobile, an emit every 25s keeps the socket "warm" and
// prevents the ingress from silently killing the connection after 60s idle.
let _heartbeatTimer = null;
const _startHeartbeat = () => {
  if (_heartbeatTimer) return;
  _heartbeatTimer = setInterval(() => {
    try {
      if (socket.connected) {
        socket.emit('client_ping', { t: Date.now() });
      }
    } catch (_) {}
  }, 25000);
};
const _stopHeartbeat = () => {
  if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null; }
};

// Track current user for re-registration on reconnect
let currentUserId = null;
// Track joined rooms for re-joining on reconnect
const activeRooms = new Set();

// Connection event handlers
socket.on('connect', () => {
  console.log('✅ Socket.IO connected:', socket.id);
  _startHeartbeat();
  // Re-register user on every connect/reconnect
  if (currentUserId) {
    socket.emit('register_user', { user_id: currentUserId }, (response) => {
      console.log('👤 Auto re-registered:', currentUserId?.substring(0, 8), response);
    });
  }
  // Re-join all active rooms on reconnect
  if (activeRooms.size > 0 && currentUserId) {
    activeRooms.forEach(roomId => {
      socket.emit('join_room', { room_id: roomId, user_id: currentUserId }, (response) => {
        console.log('🚪 Auto re-joined room:', roomId?.substring(0, 8), response);
      });
    });
  }
});

socket.on('disconnect', (reason) => {
  console.log('🔌 Socket.IO disconnected:', reason);
  _stopHeartbeat();
  // Session-48i (Bug B3): if the server chose to kick us
  // ('io server disconnect') the client won't auto-reconnect by default.
  // Force it so the handler never goes "brown" on brief backend restarts.
  if (reason === 'io server disconnect' || reason === 'transport close') {
    try { socket.connect(); } catch (_) {}
  }
});

socket.on('connect_error', (error) => {
  console.error('❌ Socket.IO connection error:', error);
});

// Point 20 — reconnect safety net. On a genuine RECONNECT (not the first
// connect), a message may have been missed during the gap — or, on a
// multi-worker/multi-pod backend, emitted to a worker this client wasn't on.
// We broadcast a DOM event so the currently-open chat can silently refetch its
// messages + unread counts (no leave/reopen needed).
socket.io.on('reconnect', (attempt) => {
  console.log('🔁 Socket reconnected after', attempt, 'attempt(s) — signalling refetch');
  try { window.dispatchEvent(new CustomEvent('socket:reconnected')); } catch (_) {}
});

// ==================== AUTO-HEAL WATCHDOG ====================
// Every 30s we sanity-check that (1) our socket is live and (2) the server's
// real-time layer is healthy. If either fails we auto-recover — user never
// has to logout/relogin or clear cache to fix a stale connection.
//
// Recovery ladder (each step tried before escalating, then holds at step 2
// forever). NOTE: this used to escalate to a silent `window.location.reload()`
// after 3 failed attempts, which wiped chat scroll position on a background
// tab switch — removed per spec: reconnect only, never reload the page.
let watchdogFailures = 0;
const WATCHDOG_INTERVAL_MS = 30000;

async function watchdogTick() {
  // Only run watchdog when we have a user (post-login) and tab is visible
  if (!currentUserId || document.visibilityState !== 'visible') return;

  const socketAlive = socket.connected;
  let serverOk = false;
  try {
    const res = await fetch(`${API}/api/health/socket`, { cache: 'no-store' });
    serverOk = res.ok;
  } catch { serverOk = false; }

  if (socketAlive && serverOk) {
    if (watchdogFailures > 0) console.log('🩺 Watchdog: recovered ✓');
    watchdogFailures = 0;
    return;
  }

  watchdogFailures += 1;
  console.warn(`🩺 Watchdog: unhealthy (socketAlive=${socketAlive}, serverOk=${serverOk}) — attempt ${watchdogFailures}`);

  if (watchdogFailures <= 1) {
    // Step 1 — nudge reconnect
    if (!socket.connected) socket.connect();
  } else {
    // Step 2+ — full transport reset, retried silently every tick for as
    // long as it takes. Never escalates to a page reload.
    try { socket.disconnect(); } catch {}
    setTimeout(() => socket.connect(), 500);
  }
}

setInterval(watchdogTick, WATCHDOG_INTERVAL_MS);

// Also re-check the moment the tab regains focus (mobile browsers freeze
// background tabs — this catches the "returned to app after 30 min" case).
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    if (!socket.connected && currentUserId) {
      console.log('🩺 Tab visible — kicking socket reconnect');
      socket.connect();
    }
  }
});

// Helper functions
export const connectSocket = (userId) => {
  currentUserId = userId;
  if (!socket.connected) {
    socket.connect();
    console.log('🔌 Connecting socket for user:', userId);
  } else {
    // Already connected, just re-register
    if (userId) {
      socket.emit('register_user', { user_id: userId }, (response) => {
        console.log('👤 User registered:', userId?.substring(0, 8), response);
      });
    }
  }
};

export const disconnectSocket = () => {
  if (socket.connected) {
    socket.disconnect();
    console.log('🔌 Socket disconnected');
  }
};

export const joinRoom = (roomId, userId) => {
  activeRooms.add(roomId);
  return new Promise((resolve) => {
    socket.emit('join_room', { room_id: roomId, user_id: userId }, (response) => {
      console.log('🚪 Joined room:', roomId, response);
      resolve(response);
    });
  });
};

export const leaveRoom = (roomId, userId) => {
  activeRooms.delete(roomId);
  return new Promise((resolve) => {
    socket.emit('leave_room', { room_id: roomId, user_id: userId }, (response) => {
      console.log('🚪 Left room:', roomId, response);
      resolve(response);
    });
  });
};

export const sendMessage = (roomId, message) => {
  return new Promise((resolve) => {
    socket.emit('send_message', { room_id: roomId, message }, (response) => {
      resolve(response);
    });
  });
};

export const sendTypingIndicator = (roomId, userId, isTyping) => {
  socket.emit('typing', { room_id: roomId, user_id: userId, is_typing: isTyping });
};

export const markMessagesRead = (roomId, messageIds, userId) => {
  socket.emit('message_read', { room_id: roomId, message_ids: messageIds, user_id: userId });
};

// Event listeners
export const onNewMessage = (callback) => {
  socket.on('new_message', callback);
};

export const onUserTyping = (callback) => {
  socket.on('user_typing', callback);
};

export const onUserOnline = (callback) => {
  socket.on('user_online', callback);
};

export const onUserOffline = (callback) => {
  socket.on('user_offline', callback);
};

export const onMessagesRead = (callback) => {
  socket.on('messages_read', callback);
};

export const onMessagesDelivered = (callback) => {
  socket.on('messages_delivered', callback);
};

// Remove event listeners
export const offNewMessage = (callback) => {
  socket.off('new_message', callback);
};

export const offUserTyping = (callback) => {
  socket.off('user_typing', callback);
};

export const offUserOnline = (callback) => {
  socket.off('user_online', callback);
};

export const offUserOffline = (callback) => {
  socket.off('user_offline', callback);
};

export const offMessagesRead = (callback) => {
  socket.off('messages_read', callback);
};

export const offMessagesDelivered = (callback) => {
  socket.off('messages_delivered', callback);
};

export const onMessageUpdated = (callback) => {
  socket.on('message_updated', callback);
};

export const offMessageUpdated = (callback) => {
  socket.off('message_updated', callback);
};

export const onMessageDeleted = (callback) => {
  socket.on('message_deleted', callback);
};

export const offMessageDeleted = (callback) => {
  socket.off('message_deleted', callback);
};

// Session-49 — Admin renames chat-header buttons in Settings; server broadcasts
// this event to every connected client so any open chat header refreshes its
// labels live (no reload / no leave-and-reopen).
export const onHeaderLabelsUpdated = (callback) => {
  socket.on('header_labels_updated', callback);
};

export const offHeaderLabelsUpdated = (callback) => {
  socket.off('header_labels_updated', callback);
};

// Emitted by the backend when this session gets evicted by the 2-concurrent-
// session cap (admin or handler) — carries a human-readable `reason` naming
// what caused it, e.g. "Signed out — you were on the oldest of 2 device
// sessions." Previously emitted but never listened for on the frontend, so
// the named warning never reached the user (they only learned via the
// generic 15s session-validation poll). Wired into AuthContext.
export const onSessionTerminated = (callback) => {
  socket.on('session_terminated', callback);
};

export const offSessionTerminated = (callback) => {
  socket.off('session_terminated', callback);
};

export default socket;
